import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

import { useAuth } from '../../context/AuthContext';
import { useLightPathEnabled } from '../../hooks/useFeatureFlags';
import { useIsGamificationParticipant } from '../../hooks/useIsGamificationParticipant';
import type { GamificationEvent } from '../../types/auth';
import { useReflectionVisible } from '../reflection/reflectionLauncher';
import { useGamificationCelebration } from './useGamificationCelebration';
// CORE-1751 — поздравление ждёт, пока на экране окно рефлексии (см. celebrationGate).
import { celebrationToShow } from './celebrationGate';

/**
 * CORE-509 — who greets the user when their Путь света moves.
 *
 * The celebration used to be an anonymous modal that appeared on its own, and
 * only in the profile. Two things were wrong with that. The ticket asks for
 * accompaniment «с помощью Agapella», and a system modal is not her; and a
 * platform-wide event was only visible on one screen, so most level-ups were
 * met by nothing at all.
 *
 * Now she leads and the modal is what she shows:
 *
 *     level_up → её поп-ап («ты на новом этапе») → «Посмотреть» → CORE-272
 *
 * That is the pattern CORE-496 already established for onboarding: anything
 * Agapella initiates should read as a conversation with her, not as the app
 * talking at you.
 *
 * This context is the seam. It owns the queue (one pending celebration at a
 * time) and one extra bit — whether the user asked for the details screen —
 * so two globally-mounted consumers can stay in step:
 *
 *   • the widget renders her panel while a celebration is pending and the
 *     details are closed;
 *   • the celebration host renders CORE-272 only once details are open.
 *
 * Mounted at the app root rather than in a profile tree, because that is the
 * whole point: she meets you wherever you are.
 */

type CelebrationContextValue = {
  /** The pending celebration, or null. One at a time, oldest first. */
  event: GamificationEvent | null;
  /** Whether the user asked to see the detailed CORE-272 screen. */
  detailsOpen: boolean;
  /** «Посмотреть» — open the details screen for the pending celebration. */
  openDetails: () => void;
  /**
   * Finish with this celebration: acknowledges it on the backend so it never
   * reappears, and clears the details screen. Called when the user closes her
   * panel OR closes the details screen — whichever ends the interaction.
   */
  dismiss: () => void;
};

const CelebrationContext = createContext<CelebrationContextValue>({
  event: null,
  detailsOpen: false,
  openDetails: () => {},
  dismiss: () => {},
});

export const useCelebration = (): CelebrationContextValue => useContext(CelebrationContext);

export const GamificationCelebrationProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const lightPathEnabled = useLightPathEnabled();
  const { isAuthenticated } = useAuth();
  // CORE-746 — a master is not a participant in the Путь света and receives no
  // congratulations. That gate used to sit in the host, which was mounted on the
  // master's own profile; with one root-level mount it belongs here, next to the
  // feature flag. The queue hook guards on both: false means it never fetches.
  const isParticipant = useIsGamificationParticipant();
  const { event: queued, dismiss: ackAndAdvance } = useGamificationCelebration(
    lightPathEnabled && isAuthenticated && isParticipant,
  );
  const event = celebrationToShow(queued, useReflectionVisible());
  const [detailsOpen, setDetailsOpen] = useState(false);

  const openDetails = useCallback(() => setDetailsOpen(true), []);

  const dismiss = useCallback(() => {
    // Close the details first so the modal can't outlive the event that fed it
    // — the next celebration in the queue would otherwise render into a screen
    // that is already open and look like it never changed.
    setDetailsOpen(false);
    ackAndAdvance();
  }, [ackAndAdvance]);

  const value = useMemo(
    () => ({ event, detailsOpen, openDetails, dismiss }),
    [event, detailsOpen, openDetails, dismiss],
  );

  return <CelebrationContext.Provider value={value}>{children}</CelebrationContext.Provider>;
};
